import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, BACKEND_URL } from "@/lib/api";

export default function ArchiveDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [img, setImg] = useState(null);
    const [tier, setTier] = useState(null);
    const [requesting, setRequesting] = useState(false);
    const [paying, setPaying] = useState(false);
    const [req, setReq] = useState({ organization: "", email: "", notes: "" });

    useEffect(() => {
        api.get(`/archive/${id}`).then((r) => {
            setImg(r.data);
            setTier(r.data.license_tiers?.[0]);
        }).catch(() => nav("/archive"));
    }, [id, nav]);

    const submitLicense = async (e) => {
        e.preventDefault();
        setRequesting(true);
        try {
            await api.post("/inquiries", {
                kind: "verification",
                name: req.organization,
                email: req.email,
                organization: req.organization,
                message: `License request for "${img.title}" (${img.id}) — tier: ${tier?.name} at $${tier?.price}. Notes: ${req.notes}`,
            });
            toast.success("License request sent. You'll receive a Stripe invoice within 24h.");
            setReq({ organization: "", email: "", notes: "" });
        } catch { toast.error("Could not submit."); } finally { setRequesting(false); }
    };

    const payLicense = async () => {
        if (!tier || !req.email || !req.organization) {
            toast.error("Enter organization + email to pay instantly.");
            return;
        }
        setPaying(true);
        try {
            const { data } = await api.post(`/archive/${img.id}/license/checkout`, {
                tier_name: tier.name, email: req.email, organization: req.organization,
                notes: req.notes, origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open checkout.");
            setPaying(false);
        }
    };

    if (!img) return <div className="min-h-screen" style={{ background: "var(--gt-bg)" }}><Header /><div className="pt-28 text-center" style={{ color: "var(--gt-text-2)" }}>Loading…</div></div>;

    return (
        <div className="min-h-screen" style={{ background: "var(--gt-bg)" }}>
            <Header />
            <section className="pt-28 pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <button onClick={() => nav("/archive")} className="gt-link text-sm mb-6" data-testid="archive-back-btn">
                        <i className="fa-solid fa-arrow-left mr-2" style={{ fontSize: 11 }}></i> Back to archive
                    </button>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-7">
                            <div className="gt-card overflow-hidden">
                                <img src={img.image_url || `${BACKEND_URL}/api/archive/image/${img.id}`} alt={img.title} className="w-full object-cover" />
                            </div>
                            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                                {[
                                    { l: "C2PA hash", v: img.c2pa_hash },
                                    { l: "GPS", v: img.gps },
                                    { l: "EXIF", v: img.exif },
                                    { l: "Date", v: img.date_shot },
                                ].map((m) => (
                                    <div key={m.l} className="gt-surface p-3">
                                        <div className="overline" style={{ fontSize: 10 }}>{m.l}</div>
                                        <div className="font-mono text-xs mt-1" style={{ color: "var(--gt-text)" }}>{m.v || "—"}</div>
                                    </div>
                                ))}
                            </div>
                            {img.consent && (
                                <div className="mt-6 gt-card p-5" data-testid="consent-block">
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="verif-dot"></span>
                                        <div className="overline">Informed consent on record</div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                        <div><div className="overline" style={{ fontSize: 10 }}>Subject</div><div>{img.consent.subject_name}</div></div>
                                        <div><div className="overline" style={{ fontSize: 10 }}>Location</div><div>{img.consent.location}</div></div>
                                        <div><div className="overline" style={{ fontSize: 10 }}>Signed</div><div>{img.consent.date_signed}</div></div>
                                    </div>
                                    <p className="mt-3 text-sm" style={{ color: "var(--gt-text-2)" }}>{img.consent.notes}</p>
                                    <div className="mt-3 overline" style={{ fontSize: 10 }}>Permissions: {img.consent.usage_permissions}</div>
                                </div>
                            )}
                        </div>

                        <div className="lg:col-span-5">
                            <div className="overline mb-3">{img.region} · {img.theme}{img.community ? ` · ${img.community}` : ""}</div>
                            <h1 className="font-display text-3xl lg:text-4xl mb-4">{img.title}</h1>
                            <p className="mb-6" style={{ color: "var(--gt-text-2)" }}>{img.caption}</p>

                            <div className="gt-card p-5 mb-5">
                                <div className="overline mb-4">License options (USD)</div>
                                <div className="space-y-2">
                                    {img.license_tiers?.map((t) => (
                                        <button
                                            key={t.name}
                                            data-testid={`license-tier-${t.name.replace(/\s/g, "-")}`}
                                            onClick={() => setTier(t)}
                                            className="w-full text-left p-3 flex items-start justify-between gap-3 transition-colors"
                                            style={{
                                                background: tier?.name === t.name ? "var(--gt-overlay)" : "transparent",
                                                border: `1px solid ${tier?.name === t.name ? "var(--gt-accent)" : "var(--gt-border)"}`,
                                            }}
                                        >
                                            <div>
                                                <div className="text-sm" style={{ color: "var(--gt-text)" }}>{t.name}</div>
                                                <div className="overline mt-1" style={{ fontSize: 10 }}>{t.usage}</div>
                                            </div>
                                            <div className="font-mono text-lg" style={{ color: "var(--gt-accent)" }}>${t.price}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <form onSubmit={submitLicense} className="gt-card p-5 space-y-3" data-testid="license-form">
                                <div className="overline">License this image</div>
                                <input required className="gt-input" placeholder="Organization" value={req.organization} onChange={(e) => setReq({ ...req, organization: e.target.value })} data-testid="license-org" />
                                <input required type="email" className="gt-input" placeholder="Contact email" value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })} data-testid="license-email" />
                                <textarea className="gt-input" placeholder="Project notes, publication, timing…" value={req.notes} onChange={(e) => setReq({ ...req, notes: e.target.value })} data-testid="license-notes" />
                                <button type="button" onClick={payLicense} disabled={paying} className="gt-btn-primary w-full py-3 text-sm" data-testid="license-pay-now">
                                    {paying ? "Opening Stripe…" : `Pay & license instantly · $${tier?.price || 150}`}
                                    <i className="fa-solid fa-arrow-right ml-2" style={{ fontSize: 10 }}></i>
                                </button>
                                <button type="submit" disabled={requesting} className="gt-btn-ghost w-full py-2.5 text-sm" data-testid="license-submit">
                                    {requesting ? "Sending…" : "Or request a quote (email)"}
                                </button>
                                <div className="overline text-center" style={{ fontSize: 10 }}>Instant payment by card via Stripe · USD</div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
            <Footer />
        </div>
    );
}
